import { useState, useEffect, useCallback } from 'react'
import { convertMarkdownToHtml } from '../util/markdown-to-html'
import { fetchMarkdownFromGCS } from '@/util/fetch-markdown-from-gcs'
import highlightMarkdownHtmlString from '../util/markdown-reference-highlighter'
import { wrapTablesWithDiv } from '@/chat-common/util/source-html-editing-functions'

interface GcsMarkdownLoaderOptions {
  gcsBucketName: string
  filePath: string
  highlights?: string[] | null
  legacy_always_cloud?: boolean
}

interface GcsMarkdownLoaderResult {
  htmlString: string | null // Or any, if the GcsMarkdown type vary.  Use a more specific type if possible.
  markdownString: string | null // Raw markdown content
  isLoading: boolean
  hasError: boolean
  retry: () => void
}

/**
 * useGcsMarkdownLoader
 *
 * Custom hook to fetch and process markdown GcsMarkdown from
 *
 * @param filePath - The path to the markdown file.
 * @returns An object containing the processed HTML string, loading state, error flag, and a retry function.
 */
const useGcsMarkdownLoader = (props: GcsMarkdownLoaderOptions): GcsMarkdownLoaderResult => {
  const { filePath, gcsBucketName, highlights, legacy_always_cloud = true } = props
  const [htmlString, setHtmlString] = useState<string | null>(null)
  const [markdownString, setMarkdownString] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [hasError, setHasError] = useState<boolean>(false)

  const fetchAndProcess = useCallback(async () => {
    setIsLoading(true)
    setHasError(false)
    setHtmlString(null)
    setMarkdownString(null)

    try {
      // Fetch markdown from GCS
      const markdown = await fetchMarkdownFromGCS(gcsBucketName, filePath, legacy_always_cloud)

      // Store raw markdown
      setMarkdownString(markdown)

      // Convert markdown to HTML
      let html = await convertMarkdownToHtml(markdown)

      // Highlight text if necessary
      if (highlights) {
        html = await highlightMarkdownHtmlString(html, highlights)
      }

      // Wrap tables to allow horizontal scrolling and nice table formatting
      html = wrapTablesWithDiv(html)

      // Update local state with HTML
      setHtmlString(html)
    } catch (err) {
      console.error('Error in useGcsMarkdown:', err)
      setHasError(true)
    } finally {
      setIsLoading(false)
    }
  }, [filePath, gcsBucketName, highlights, legacy_always_cloud])

  // Initial fetch and process on mount or when filePath changes
  useEffect(() => {
    fetchAndProcess()
  }, [fetchAndProcess])

  return { htmlString, markdownString, isLoading, hasError, retry: fetchAndProcess }
}

export default useGcsMarkdownLoader
